import Image from "next/image";
import dynamic from 'next/dynamic';
import { sanitizeHTML } from "@/lib/utils";
import { getReadableImageUrl } from "@/lib/directus";
import Link from "next/link";

const AnimatedSection = dynamic(() => import('../../components/Animations/useAnimations'), { ssr: false });
const ScreenAwareContent = dynamic(() => import('../../utils/screenAwareContent'), { 
  ssr: false,
  loading: () => <div className="min-h-screen h-screen" /> // Simple loading state
});

interface RhinoplastyDetailsData {
  heading?: {
    text?: string;
    title?: string;
  };
  text?: {
    text?: string;
  };
  image?: {
    alt: string;
    image_file: {
      image: {
        id: string;
        filename_download: string;
      };
    };
  };
  buttons?: {
    menu_items_id: {
      href: string;
      label: string;
    };
  }[];
  video?: {
    id: string;
    filename_download: string;
  };
}

export const fields = [
  // headings
  "heading.text",
  "heading.title",

  // text
  "text.text",

  // image (arrow)
  "image.alt",
  "image.image_file.image.id",
  "image.image_file.image.filename_download",

  // buttons
  "buttons.menu_items_id.href",
  "buttons.menu_items_id.label",
  
  // video
  "video.id",
  "video.filename_download",
] as const;

interface RhinoplastyDetailsProps {
  data?: RhinoplastyDetailsData;
  zIndex: number;
  thresholds: number[][];
  transitionTime?: number;
  mobileThresholds: number[][];
  helperRotation?: number;
}

export default function RhinoplastyDetails({ data, zIndex, thresholds, transitionTime, mobileThresholds, helperRotation }: RhinoplastyDetailsProps) {
  const arrowImageId = data?.image?.image_file?.image?.id || "";
  const arrowImageFilename = data?.image?.image_file?.image?.filename_download || "";
  const arrowImageAlt = data?.image?.alt || "Arrow";
  
  // Video URL from Directus
  const videoUrl = `${process.env.NEXT_PUBLIC_DIRECTUS_URL}/assets/${data?.video?.id}/${data?.video?.filename_download}`;

  return (
    <ScreenAwareContent
      supportedContent={
        <AnimatedSection
          helperRotation={helperRotation}
          className="h-screen overflow-hidden fixed w-full pt-16 xl:pt-32  top-0 left-0"
          thresholds={[
            thresholds?.[0]?.[0],
            thresholds?.[thresholds.length - 1]?.[1],
          ]}
          mobileThresholds={[
            mobileThresholds?.[0]?.[0],
            mobileThresholds?.[mobileThresholds.length - 1]?.[1],
          ]}
          zIndex={zIndex}
          withZIndex={true}
          variants={{
            desktop: {
              visible: { y: 0, x:0, opacity: 1 },
              above: { y: "100vh", x:0, opacity: 1 },
              below: { y: "-100vh", x:0, opacity: 1 },
            },
            mobile: {
              visible: { y: 0, x:0, opacity: 1 },
              above: { y: 0, x:"100vw", opacity: 0 },
              below: { y: "-100vh", x:0, opacity: 1 },
            }
          }}
          transitionTime={transitionTime}
        > 
          <div className=" h-full  overflow-hidden relative w-full" >
            <div className="absolute w-full h-full top-0 left-0 inset-0 z-10">
              <video
                autoPlay
                muted
                loop
                playsInline
									preload="auto"
                className="absolute top-0 left-0 z-10 inset-0 w-full h-full object-cover"
              >
                <source src={videoUrl} type="video/mp4" />
              </video>

              <div className="absolute w-full h-full top-0 left-0 inset-0 bg-primary/50  lg:bg-primary/30 z-20" />
            </div>

            {/* Content Container */}
            <div className="relative z-20 h-full gl-container flex items-start lg:items-center">
              <div>
                <div className="lg:bg-white/70 lg:rounded-2xl lg:p-16 lg:max-w-[95%] lg:mx-auto  gl-desktop-scale">
                  {/* Heading */}
                  <h2 className="text-[2rem] font-essonnes font-light leading-tight md:text-5xl lg:text-[3.375rem] text-white lg:text-primary lg:text-center mb-6">
                    {data?.heading?.text || data?.heading?.title}
                  </h2>

                  {/* Description */}
                  <div 
                    className="text-sm lg:text-xl text-white lg:text-primary font-greycliff leading-tight tracking-tight mb-6 lg:text-center lg:mb-12"
                    dangerouslySetInnerHTML={sanitizeHTML(data?.text?.text || "")}
                  />

                  {/* Procedures Grid */}
                  <div className="flex flex-wrap space-y-2 lg:space-y-4 lg:justify-center text-sm">
                    {data?.buttons?.map((button, index) => (
                      <Link
                        key={index}
                        href={button.menu_items_id.href || '#'}
                        className={`
                        px-2 py-2 mr-2 mt-4 lg:px-6 lg:py-3 rounded-full border font-greycliff text-lg lg:text-2xl flex items-center
                        border-white lg:border-primary text-white lg:text-primary
                        transition-colors 
                        hover:bg-[#dac79b] hover:text-primary hover:border-transparent
                        active:bg-[#dac79b] active:text-primary active:border-transparent
                      `}
                      >
                        {button.menu_items_id.label}
                        {arrowImageId && (
                          <Image 
                            src={getReadableImageUrl(arrowImageId, arrowImageFilename)}
                            alt={arrowImageAlt}
                            width={26} 
                            height={27} 
                            className="ml-2 inline-block w-5 h-5 lg:w-[26px] lg:h-[27px] [filter:brightness(0)_invert(1)] lg:filter-none" 
                            quality={90}
                          />
                        )}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </AnimatedSection>
      }
      fallbackContent={
        <section className="overflow-hidden w-full"> 
          <div className=" h-full  overflow-hidden relative w-full" >
            <div className="absolute w-full h-full top-0 left-0 inset-0 z-10">
              <video
                autoPlay
                muted
                loop
                playsInline
									preload="auto"
                className="absolute top-0 left-0 z-10 inset-0 w-full h-full object-cover"
              >
                <source src={videoUrl} type="video/mp4" />
              </video>

              <div className="absolute w-full h-full top-0 left-0 inset-0 bg-primary/50  xl:bg-primary/30 z-20" />
            </div>

            {/* Content Container */}
            <div className="relative z-20 h-full gl-container flex items-start lg:items-center">
              <div>
                <div className="xl:bg-white/70 xl:rounded-2xl xl:p-16 xl:max-w-[80%] xl:mx-auto  gl-desktop-scale">
                  {/* Heading */}
                  <h2 className="text-[2rem] font-essonnes font-light leading-tight md:text-5xl lg:text-[3.375rem] text-white xl:text-primary xl:text-center mb-6">
                    {data?.heading?.text || data?.heading?.title}
                  </h2>

                  {/* Description */}
                  <div 
                    className="text-sm xl:text-xl text-white xl:text-primary font-greycliff leading-tight tracking-tight mb-6 xl:text-center xl:mb-12"
                    dangerouslySetInnerHTML={sanitizeHTML(data?.text?.text || "")}
                  />

                  {/* Procedures Grid */}
                  <div className="flex flex-wrap space-y-2 lg:space-y-4 lg:justify-center text-sm">
                    {data?.buttons?.map((button, index) => (
                      <Link
                        key={index}
                        href={button.menu_items_id.href || '#'}
                        className={`
                        px-2 py-2 mr-2 mt-4 xl:px-6 xl:py-3 rounded-full border font-greycliff text-lg xl:text-2xl flex items-center
                        border-white xl:border-primary text-white xl:text-primary
                        transition-colors 
                        hover:bg-[#dac79b] hover:text-primary hover:border-transparent
                        active:bg-[#dac79b] active:text-primary active:border-transparent
                      `}
                      >
                        {button.menu_items_id.label}
                        {arrowImageId && (
                          <Image 
                            src={getReadableImageUrl(arrowImageId, arrowImageFilename)}
                            alt={arrowImageAlt}
                            width={26} 
                            height={27} 
                            className="ml-2 inline-block w-5 h-5 xl:w-[26px] xl:h-[27px] [filter:brightness(0)_invert(1)] xl:filter-none" 
                            quality={90}
                          />
                        )}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      }
    />
  );
}
